// src/admin/pages/employee/EmployeeDrawer.tsx
import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Person as PersonIcon, AccountBalance as BankIcon } from '@mui/icons-material';
import { Employee, EmployeeFormData, EMPLOYEE_ROLES, EMPLOYEE_STATUSES } from '../../../enums/employee';
import { Warehouse } from '../../../enums/product';
import DrawerFooter from '../../components/DrawerFooter';

interface EmployeeDrawerProps {
  isOpen: boolean;
  mode: 'add' | 'edit';
  employee: Employee | null;
  branches: Warehouse[];
  onClose: () => void;
  onSave: (formData: EmployeeFormData) => Promise<void>;
}

const EMPTY_FORM: EmployeeFormData = {
  fullName: '', nic: '', email: '', phone: '', address: '', jobRole: 'Sales Executive',
  branchId: '', basicSalary: '', commissionRate: '0', joinDate: new Date().toISOString().split('T')[0],
  status: 'Active', bankName: '', bankAccountNumber: '', notes: '',
};

const EmployeeDrawer: React.FC<EmployeeDrawerProps> = ({ isOpen, mode, employee, branches, onClose, onSave }) => {
  const [formData, setFormData] = useState<EmployeeFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && employee && mode === 'edit') {
      setFormData({
        fullName: employee.fullName, nic: employee.nic || '', email: employee.email || '', phone: employee.phone,
        address: employee.address || '', jobRole: employee.jobRole, branchId: employee.branchId || '',
        basicSalary: String(employee.basicSalary), commissionRate: String(employee.commissionRate || 0),
        joinDate: employee.joinDate, status: employee.status, bankName: employee.bankName || '',
        bankAccountNumber: employee.bankAccountNumber || '', notes: employee.notes || '',
      });
    } else if (isOpen && mode === 'add') {
      setFormData(EMPTY_FORM);
    }
  }, [isOpen, employee, mode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const isValid = formData.fullName.trim() && formData.phone.trim() && formData.basicSalary;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: '100%', md: 650 }, maxWidth: 650 } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6">{mode === 'add' ? 'Add New Employee' : 'Edit Employee'}</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content + footer */}
          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    Personal Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Full Name *" name="fullName" value={formData.fullName} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Phone *" name="phone" value={formData.phone} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Email" name="email" value={formData.email} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="NIC / National ID" name="nic" value={formData.nic} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleChange} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Employment Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Job Role" name="jobRole" value={formData.jobRole} onChange={handleChange as any}>
                        {EMPLOYEE_ROLES.map((r) => (
                          <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Branch" name="branchId" value={formData.branchId} onChange={handleChange as any}>
                        <MenuItem value="">Unassigned</MenuItem>
                        {branches.map((b) => (
                          <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth type="number" label="Basic Salary *" name="basicSalary" value={formData.basicSalary} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Commission %"
                        name="commissionRate"
                        value={formData.commissionRate}
                        onChange={handleChange}
                        helperText="For sales staff, per car sold"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Join Date"
                        name="joinDate"
                        value={formData.joinDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Status" name="status" value={formData.status} onChange={handleChange as any}>
                        {EMPLOYEE_STATUSES.map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <BankIcon sx={{ mr: 1 }} />
                    Banking &amp; Notes
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Bank Name" name="bankName" value={formData.bankName} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Bank Account No." name="bankAccountNumber" value={formData.bankAccountNumber} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={3} label="Notes" name="notes" value={formData.notes} onChange={handleChange} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>

            <DrawerFooter>
              <Button onClick={onClose} variant="outlined" disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving || !isValid}
                startIcon={saving && <CircularProgress size={16} />}
              >
                {saving ? 'Saving...' : mode === 'add' ? 'Add Employee' : 'Save Changes'}
              </Button>
            </DrawerFooter>
          </Box>
        </Box>
      </Drawer>

      <Backdrop
        open={saving}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {mode === 'add' ? 'Adding Employee…' : 'Updating Employee…'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
};

export default EmployeeDrawer;
